import {
  type ScheduleItem,
  parseISODate,
  toISODate,
  sortScheduleItemsByDate,
  isScheduleItemIncluded,
  getBarColor,
  getContrastColor,
  getTaskDisplayTitle,
  formatDateLabel,
} from '@/lib/scheduleUtils';

const PRINT_ROW_H = 30;
const PRINT_DAY_W = 14;
const PRINT_HEADER_H = 36;
const LEFT_PANEL_W = 340;
const PRINT_PAGE_MAX_W = 1500;
const MIN_PRINT_DAY_W = 6;
const PRINT_BAR_H = 18;
/** Rough px per character at 9px print font for label width estimates. */
const PRINT_LABEL_CHAR_W = 5.4;

const GANTT_AUTO_PRINT_SCRIPT = `<script>
(function () {
  function runPrint() {
    try {
      window.focus();
      window.print();
    } catch (e) {
      console.warn('Gantt print failed', e);
    }
  }
  function schedule() {
    setTimeout(runPrint, 400);
  }
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
})();
</script>`;

/** Tasks included in Gantt print (matches on-screen inclusion rules). */
export function getPrintableScheduleItems(items: ScheduleItem[]): ScheduleItem[] {
  return sortScheduleItemsByDate(items.filter(isScheduleItemIncluded));
}

export interface GanttPrintOptions {
  projectName: string;
  items: ScheduleItem[];
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  exportedAt?: Date;
  /** Show a screen-only hint to save as PDF via the browser print dialog. */
  showPdfHint?: boolean;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function dayOffset(dateStr: string, rangeStart: string): number {
  const s = parseISODate(rangeStart);
  const d = parseISODate(dateStr);
  return Math.round((d.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
}

function computePrintRange(items: ScheduleItem[]): { rangeStart: string; dayCount: number } {
  const withDates = items.filter((i) => i.start_date);
  const starts = withDates.map((i) => i.start_date as string);
  const ends = withDates.map((i) => i.end_date).filter(Boolean) as string[];

  if (starts.length === 0) {
    const today = toISODate(new Date());
    return { rangeStart: today, dayCount: 30 };
  }

  const minS = starts.reduce((a, b) => (a < b ? a : b));
  const maxE = ends.length > 0 ? ends.reduce((a, b) => (a > b ? a : b)) : minS;
  const s = new Date(parseISODate(minS));
  s.setDate(s.getDate() - 7);
  const e = new Date(parseISODate(maxE));
  e.setDate(e.getDate() + 28);
  const count = Math.max(30, Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1);
  return { rangeStart: toISODate(s), dayCount: count };
}

function buildDayInfos(rangeStart: string, dayCount: number) {
  const start = parseISODate(rangeStart);
  const todayIso = toISODate(new Date());
  const arr: { dateStr: string; isToday: boolean }[] = [];
  for (let i = 0; i < dayCount; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dateStr = toISODate(d);
    arr.push({ dateStr, isToday: dateStr === todayIso });
  }
  return arr;
}

interface PrintBarLabelParts {
  title: string;
  full: string;
  datesAndDays: string;
  compact: string;
}

function buildPrintBarLabelParts(
  item: ScheduleItem,
  barStart: string,
  barEnd: string
): PrintBarLabelParts {
  const title = getTaskDisplayTitle(item);
  const start = formatDateLabel(barStart);
  const end = formatDateLabel(barEnd);
  const days =
    typeof item.duration_days === 'number' && item.duration_days > 0
      ? item.duration_days
      : Math.max(1, dayOffset(barEnd, barStart) + 1);
  const dateRange = barEnd !== barStart ? `${start} – ${end}` : start;
  const datesAndDays = `${dateRange}  ${days}d`;
  return {
    title,
    full: `${title}  ${datesAndDays}`,
    datesAndDays,
    compact: `${title}  ${start}`,
  };
}

function estimateLabelWidthPx(text: string): number {
  return Math.ceil(text.length * PRINT_LABEL_CHAR_W);
}

function renderPrintBarLabels(
  labels: PrintBarLabelParts,
  barLeft: number,
  barWidth: number,
  labelColor: string
): { insideHtml: string; outsideHtml: string } {
  const innerPad = 10;
  const fullWidth = estimateLabelWidthPx(labels.full) + innerPad;
  const titleWidth = estimateLabelWidthPx(labels.title) + innerPad;
  const outsideLeft = barLeft + barWidth + 4;

  if (barWidth >= Math.max(80, fullWidth)) {
    return {
      insideHtml: `<span class="bar-label bar-label-inside" style="color:${labelColor}">${escapeHtml(labels.full)}</span>`,
      outsideHtml: '',
    };
  }

  if (barWidth >= Math.max(44, titleWidth)) {
    return {
      insideHtml: `<span class="bar-label bar-label-inside bar-label-title-only" style="color:${labelColor}">${escapeHtml(labels.title)}</span>`,
      outsideHtml: `<span class="bar-label-outside" style="left:${outsideLeft}px">${escapeHtml(labels.datesAndDays)}</span>`,
    };
  }

  if (barWidth >= 36) {
    return {
      insideHtml: `<span class="bar-label bar-label-inside bar-label-title-only" style="color:${labelColor}">${escapeHtml(labels.compact)}</span>`,
      outsideHtml: '',
    };
  }

  return {
    insideHtml: '',
    outsideHtml: `<span class="bar-label-outside" style="left:${outsideLeft}px">${escapeHtml(labels.full)}</span>`,
  };
}

function buildMonthGroups(dayInfos: { dateStr: string }[]) {
  const groups: { month: string; dayCount: number }[] = [];
  let current: { month: string; dayCount: number } | null = null;
  for (const di of dayInfos) {
    const d = parseISODate(di.dateStr);
    const month = d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    if (!current || current.month !== month) {
      if (current) groups.push(current);
      current = { month, dayCount: 1 };
    } else {
      current.dayCount++;
    }
  }
  if (current) groups.push(current);
  return groups;
}

export function buildGanttPrintHtml(options: GanttPrintOptions): string {
  const {
    projectName,
    items,
    dateRangeStart,
    dateRangeEnd,
    exportedAt = new Date(),
    showPdfHint = false,
  } = options;

  const sorted = getPrintableScheduleItems(items);
  const { rangeStart, dayCount } = computePrintRange(sorted);
  const dayInfos = buildDayInfos(rangeStart, dayCount);
  const monthGroups = buildMonthGroups(dayInfos);
  const naturalTimelineWidth = dayCount * PRINT_DAY_W;
  const naturalTotalWidth = LEFT_PANEL_W + naturalTimelineWidth;
  const dayW =
    naturalTotalWidth > PRINT_PAGE_MAX_W
      ? Math.max(MIN_PRINT_DAY_W, (PRINT_PAGE_MAX_W - LEFT_PANEL_W) / dayCount)
      : PRINT_DAY_W;
  const timelineWidth = dayCount * dayW;
  const totalWidth = LEFT_PANEL_W + timelineWidth;
  const todayIso = toISODate(new Date());
  const todayOff = dayOffset(todayIso, rangeStart);
  const showTodayLine = todayOff >= 0 && todayOff < dayCount;

  const rangeLabel =
    dateRangeStart && dateRangeEnd
      ? `${formatDateLabel(dateRangeStart)} – ${formatDateLabel(dateRangeEnd)}`
      : `${formatDateLabel(rangeStart)} – ${formatDateLabel(dayInfos[dayInfos.length - 1]?.dateStr ?? rangeStart)}`;

  const monthHeaderCells = monthGroups
    .map(
      (mg) =>
        `<div class="month-cell" style="width:${mg.dayCount * dayW}px">${escapeHtml(mg.month)}</div>`
    )
    .join('');

  const dayHeaderCells = dayInfos
    .map((di) => {
      const d = parseISODate(di.dateStr);
      const cls = di.isToday ? 'day-cell today' : 'day-cell';
      return `<div class="${cls}" style="width:${dayW}px" title="${escapeHtml(di.dateStr)}">${d.getDate()}</div>`;
    })
    .join('');

  const leftRows = sorted
    .map((item) => {
      const title = getTaskDisplayTitle(item);
      const completeMark = item.is_complete || item.progress >= 100 ? '✓' : '';
      return `
        <div class="gantt-row left-row">
          <div class="cell-no">${item.task_no ?? ''}</div>
          <div class="cell-title" title="${escapeHtml(title)}">${escapeHtml(title)}</div>
          <div class="cell-start">${item.start_date ? escapeHtml(formatDateLabel(item.start_date)) : '—'}</div>
          <div class="cell-days">${item.duration_days}d</div>
          <div class="cell-prog">${item.progress}%</div>
          <div class="cell-done">${completeMark}</div>
        </div>`;
    })
    .join('');

  const chartRows = sorted
    .map((item) => {
      if (!item.start_date) {
        const title = getTaskDisplayTitle(item);
        return `
          <div class="gantt-row chart-row">
            <div class="no-date-label">${escapeHtml(title)} — No date assigned</div>
          </div>`;
      }

      const barStart = item.start_date;
      const barEnd = item.end_date || item.start_date;
      const sOff = dayOffset(barStart, rangeStart);
      const eOff = dayOffset(barEnd, rangeStart);
      const barLeft = Math.max(0, sOff * dayW);
      const barWidth = Math.max(dayW, (eOff - sOff + 1) * dayW);
      const barColor = getBarColor(item);
      const labelColor = getContrastColor(barColor);
      const labels = buildPrintBarLabelParts(item, barStart, barEnd);
      const renderedBarWidth = Math.max(4, barWidth - 2);
      const { insideHtml, outsideHtml } = renderPrintBarLabels(
        labels,
        barLeft + 1,
        renderedBarWidth,
        labelColor
      );

      return `
        <div class="gantt-row chart-row">
          <div
            class="gantt-bar"
            style="left:${barLeft + 1}px;width:${renderedBarWidth}px;background:${barColor};color:${labelColor}"
            title="${escapeHtml(labels.full)}"
          >
            ${insideHtml}
            <div class="bar-progress" style="width:${Math.min(100, Math.max(0, item.progress))}%"></div>
          </div>
          ${outsideHtml}
        </div>`;
    })
    .join('');

  const pdfHint = showPdfHint
    ? `<div class="no-print pdf-hint">Choose <strong>Save as PDF</strong> in the print dialog to export this Gantt chart.</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(projectName)} – Schedule</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #1a1a1a;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @page {
      size: A3 landscape;
      margin: 8mm;
    }
    @media print {
      html, body { background: #fff; overflow: visible; }
      .no-print { display: none !important; }
      .gantt-print-page { padding: 0; }
      .gantt-print-body { overflow: visible; }
      .gantt-row { break-inside: avoid; page-break-inside: avoid; }
      .bar-label-inside,
      .bar-label-outside,
      .no-date-label {
        font-size: 10px;
      }
    }
    .pdf-hint {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
      background: #fef0e7;
      border-bottom: 1px solid #fdba74;
      padding: 10px 16px;
      font-size: 13px;
      color: #92400e;
      text-align: center;
    }
    .gantt-print-page {
      padding: 16px 20px 24px;
    }
    .gantt-print-header {
      margin-bottom: 14px;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 10px;
    }
    .gantt-print-header h1 {
      margin: 0 0 4px;
      font-size: 20px;
      font-weight: 700;
      color: #1a1a1a;
    }
    .gantt-print-header .header-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 24px;
      margin-top: 6px;
    }
    .gantt-print-header .meta-line {
      margin: 0;
      font-size: 11px;
      color: #374151;
      line-height: 1.4;
    }
    .gantt-print-header .meta-label {
      font-weight: 700;
      color: #6b7280;
      margin-right: 4px;
    }
    .gantt-print-chart {
      width: ${totalWidth}px;
      max-width: 100%;
    }
    .gantt-print-body {
      display: flex;
      align-items: flex-start;
      border: 1px solid #e5e7eb;
      background: #fff;
      overflow: visible;
    }
    .gantt-print-left {
      width: ${LEFT_PANEL_W}px;
      flex-shrink: 0;
      border-right: 1px solid #e5e7eb;
    }
    .gantt-print-right {
      position: relative;
      width: ${timelineWidth}px;
      flex-shrink: 0;
      overflow: visible;
    }
    .left-header, .timeline-header {
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6b7280;
    }
    .left-header {
      display: grid;
      grid-template-columns: 32px 1fr 68px 36px 36px 24px;
      align-items: center;
      height: ${PRINT_HEADER_H}px;
      padding: 0 6px;
      gap: 4px;
    }
    .timeline-header {
      height: ${PRINT_HEADER_H}px;
    }
    .month-row {
      display: flex;
      height: 18px;
      border-bottom: 1px solid #e5e7eb;
    }
    .month-cell {
      flex-shrink: 0;
      font-size: 9px;
      font-weight: 700;
      color: #374151;
      padding: 2px 4px;
      border-right: 1px solid #e5e7eb;
      overflow: hidden;
      white-space: nowrap;
    }
    .day-row {
      display: flex;
      height: 18px;
    }
    .day-cell {
      flex-shrink: 0;
      font-size: 8px;
      font-weight: 600;
      text-align: center;
      line-height: 18px;
      border-right: 1px solid #f3f4f6;
      color: #374151;
    }
    .day-cell.today {
      background: #fef2f2;
      color: #ef4444;
    }
    .gantt-row {
      height: ${PRINT_ROW_H}px;
      border-bottom: 1px solid #f3f4f6;
    }
    .left-row {
      display: grid;
      grid-template-columns: 32px 1fr 68px 36px 36px 24px;
      align-items: center;
      gap: 4px;
      padding: 0 6px;
      font-size: 10px;
    }
    .cell-title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 500;
      color: #1a1a1a;
    }
    .cell-no, .cell-start, .cell-days, .cell-prog, .cell-done {
      color: #374151;
      text-align: center;
      font-size: 9px;
    }
    .cell-done { color: #059669; font-weight: 700; }
    .chart-rows-wrap {
      position: relative;
      overflow: visible;
    }
    .chart-row {
      position: relative;
      width: ${timelineWidth}px;
      overflow: visible;
    }
    .today-line {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 1px;
      background: rgba(239, 68, 68, 0.75);
      z-index: 5;
      pointer-events: none;
    }
    .gantt-bar {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      height: ${PRINT_BAR_H}px;
      border-radius: 2px;
      overflow: hidden;
      border: 1px solid rgba(0, 0, 0, 0.12);
      z-index: 2;
    }
    .bar-label-inside {
      position: relative;
      z-index: 2;
      display: block;
      padding: 0 5px;
      font-size: 9px;
      font-weight: 600;
      line-height: ${PRINT_BAR_H - 2}px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .bar-label-outside {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 4;
      padding: 0 2px;
      font-size: 9px;
      font-weight: 600;
      line-height: 1.25;
      color: #1a1a1a;
      white-space: nowrap;
      pointer-events: none;
    }
    .bar-progress {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.28);
      z-index: 1;
      pointer-events: none;
    }
    .no-date-label {
      padding: 0 8px;
      font-size: 9px;
      color: #6b7280;
      line-height: ${PRINT_ROW_H}px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .empty-state {
      padding: 24px;
      text-align: center;
      color: #6b7280;
      font-size: 13px;
    }
  </style>
</head>
<body>
  ${pdfHint}
  <div class="gantt-print-page">
    <header class="gantt-print-header">
      <h1>${escapeHtml(projectName)}</h1>
      <div class="header-meta">
        <p class="meta-line"><span class="meta-label">Schedule range:</span>${escapeHtml(rangeLabel)}</p>
        <p class="meta-line"><span class="meta-label">Tasks:</span>${sorted.length} included</p>
        <p class="meta-line"><span class="meta-label">Exported:</span>${escapeHtml(exportedAt.toLocaleString(undefined))}</p>
      </div>
    </header>
    ${
      sorted.length === 0
        ? '<div class="empty-state">No included schedule tasks to print.</div>'
        : `
    <div class="gantt-print-chart">
      <div class="gantt-print-body">
        <div class="gantt-print-left">
          <div class="left-header">
            <span>#</span><span>Task</span><span>Start</span><span>Days</span><span>%</span><span>✓</span>
          </div>
          ${leftRows}
        </div>
        <div class="gantt-print-right">
          <div class="timeline-header">
            <div class="month-row">${monthHeaderCells}</div>
            <div class="day-row">${dayHeaderCells}</div>
          </div>
          <div class="chart-rows-wrap" style="width:${timelineWidth}px">
            ${showTodayLine ? `<div class="today-line" style="left:${todayOff * dayW + dayW / 2}px"></div>` : ''}
            ${chartRows}
          </div>
        </div>
      </div>
    </div>`
    }
  </div>
  ${GANTT_AUTO_PRINT_SCRIPT}
</body>
</html>`;
}

export class GanttPrintError extends Error {
  constructor(
    message: string,
    public readonly code: 'POPUP_BLOCKED' | 'PRINT_FAILED'
  ) {
    super(message);
    this.name = 'GanttPrintError';
  }
}

export function openGanttPrintDialog(options: GanttPrintOptions): Promise<void> {
  const printable = getPrintableScheduleItems(options.items);
  if (printable.length === 0) {
    return Promise.reject(new GanttPrintError('No printable tasks', 'PRINT_FAILED'));
  }

  const html = buildGanttPrintHtml(options);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const printWindow = window.open(blobUrl, '_blank');

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    return Promise.reject(new GanttPrintError('Popup blocked', 'POPUP_BLOCKED'));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout>;

    const finish = (err?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
      if (err) reject(err);
      else resolve();
    };

    fallbackTimer = window.setTimeout(() => finish(), 2000);

    try {
      printWindow.addEventListener(
        'load',
        () => {
          window.setTimeout(() => finish(), 600);
        },
        { once: true }
      );
    } catch {
      finish(new GanttPrintError('Print failed', 'PRINT_FAILED'));
    }
  });
}
